import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import posthog from 'posthog-js'

interface PostHogContextType {
  track: (event: string, properties?: Record<string, any>) => void
  identify: (userId: string, properties?: Record<string, any>) => void
  reset: () => void
  isInitialized: boolean
}

const PostHogContext = createContext<PostHogContextType | undefined>(undefined)

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || 'phc_test_key'
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com'

interface PostHogProviderProps {
  children: ReactNode
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = React.useState(false)

  useEffect(() => {
    // Initialize PostHog only if we have a valid key and it's not already initialized
    if (POSTHOG_KEY && POSTHOG_KEY !== 'phc_test_key' && !posthog.__loaded) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        loaded: (posthog) => {
          setIsInitialized(true)
        },
        // Development configuration
        debug: import.meta.env.DEV,
        // Respect user privacy
        respect_dnt: true,
        // Capture pageviews automatically
        capture_pageview: true,
        // Capture performance metrics
        capture_performance: true,
        // Session recording (optional - can be disabled)
        disable_session_recording: false,
        // Cross-domain linking for web/electron compatibility
        cross_subdomain_cookie: false,
        // Security
        secure_cookie: true,
        // Session recording privacy settings
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            email: true,
          },
          maskTextSelector: '.ph-no-capture, [data-private="true"], .file-content-private',
          blockClass: 'ph-block',
          blockSelector: '.ph-no-capture, [data-private="true"], .file-content-private',
          ignoreClass: 'ph-ignore',
        },
      })
    } else {
      // For development or when no key is provided
      setIsInitialized(false)
    }

    return () => {
      // Cleanup on unmount
      if (posthog.__loaded) {
        posthog.reset()
      }
    }
  }, [])

  const track = (event: string, properties?: Record<string, any>) => {
    if (isInitialized && posthog.__loaded) {
      posthog.capture(event, properties)
    }
  }

  const identify = (userId: string, properties?: Record<string, any>) => {
    if (isInitialized && posthog.__loaded) {
      posthog.identify(userId, properties)
    }
  }

  const reset = () => {
    if (isInitialized && posthog.__loaded) {
      posthog.reset()
    }
  }

  const contextValue: PostHogContextType = {
    track,
    identify,
    reset,
    isInitialized,
  }

  return <PostHogContext.Provider value={contextValue}>{children}</PostHogContext.Provider>
}

export const usePostHog = (): PostHogContextType => {
  const context = useContext(PostHogContext)
  if (context === undefined) {
    throw new Error('usePostHog must be used within a PostHogProvider')
  }
  return context
}
